import type { FeatureContext } from './featureContext';
import { initCardBinder, type CardBinderHandle } from '../cardBinder';
import { initPackOpening, type PackOpeningHandle } from '../packOpening';
import { initCardPreview } from '../cardPreview';
import type { StarterRecipeId } from '../sim';

/** Injectable seams for unit tests; defaults are the real modules. */
export interface CardsInstallDeps {
  initCardBinder?: typeof initCardBinder;
  initPackOpening?: typeof initPackOpening;
  initCardPreview?: typeof initCardPreview;
}

/**
 * The trading card game's client feature (PRD #493): the **Card Binder** gump
 * (Slice 3) plus pack **sales + opening** (Slice 4).
 *
 * - The **Card Binder** (`#card-binder`) is the draggable window onto the collection,
 *   deck building, and pack count, opened from the toolbar's **Cards** button and the
 *   `/cards` chat shortcut. Its Packs tab carries the whole pack economy: a **Buy**
 *   button that sends a `BuyCardPack` command (the server is authoritative on
 *   `PACK_PRICE_GOLD`, gold, and the reserved-only rule) and an **Open a pack** button
 *   that sends `OpenCardPack`; the server rolls five seeded cards, banks them, and
 *   replies with the reveal (for the flip modal) + fresh `CardState`. There is no
 *   vendor NPC - Card Master Carl survives only as the practice AI persona.
 * - The Binder's Recipes tab starts a **free AI practice match** against Carl (PRD
 *   #493 Slice 6) - a `CardDuelPractice` command that the card-duel installer's battle
 *   gump opens; practice is free, no-stakes, and open to anonymous learners too.
 * - The Binder's Challenges tab starts reserved-only 100-health Carl matches whose
 *   first-win reward and daily cap are resolved entirely by the server.
 * - The **pack-opening modal** (`#pack-opening`) flips the five revealed cards one at a
 *   time (shared card-back art, a rarity burst, and a legendary gold-flash sting),
 *   driven by the owner-only `PackOpened` push.
 *
 * The gumps hold no game logic (ADR-0268): they render the authoritative `CardState`
 * the server pushes and send intents (`OpenBinder` / `SaveCardDecks` / the two pack
 * commands). The `cardState` event feeds the Binder; `packOpened` feeds the modal.
 * Server-authoritative throughout - the client never sets its own gold or roll results.
 */
export function installCards(ctx: FeatureContext, deps: CardsInstallDeps = {}): void {
  let enabled = false;
  const presentedListingAcceptances = new Set<string>();

  const openPack = (): void => {
    const playerId = ctx.playerId();
    if (playerId !== undefined) ctx.client()?.sendCommand({ kind: 'OpenCardPack', entity: playerId });
  };

  const buyPack = (): void => {
    if (!enabled) return;
    const playerId = ctx.playerId();
    if (playerId !== undefined) ctx.client()?.sendCommand({ kind: 'BuyCardPack', entity: playerId });
  };

  // Start a free AI practice match against Carl (PRD #493 Slice 6): the server starts the
  // duel against the greedy card AI and pushes the per-seat `CardDuelState`, which the
  // card-duel installer's battle gump opens. Gated by `enabled` like every card intent.
  const startPractice = (
    learnerRecipeId?: StarterRecipeId,
    opponentRecipeId?: StarterRecipeId,
    learnerDeckIndex?: number,
  ): void => {
    if (!enabled) return;
    const playerId = ctx.playerId();
    if (playerId !== undefined) {
      ctx.client()?.sendCommand({
        kind: 'CardDuelPractice',
        entity: playerId,
        ...(learnerRecipeId ? { learnerRecipeId } : {}),
        ...(opponentRecipeId ? { opponentRecipeId } : {}),
        ...(learnerDeckIndex !== undefined ? { learnerDeckIndex } : {}),
      });
    }
  };

  const binder: CardBinderHandle = (deps.initCardBinder ?? initCardBinder)({
    doc: ctx.ui,
    onOpenBinder: () => {
      if (enabled) ctx.client()?.openBinder();
    },
    onSaveDecks: (decks) => {
      if (enabled) ctx.client()?.saveCardDecks(decks);
    },
    onOpenPack: () => openPack(),
    onBuyPack: () => buyPack(),
    onChooseStarter: (recipeId) => {
      if (enabled) ctx.client()?.chooseStarterRecipe(recipeId);
    },
    onPracticeRecipe: (recipeId, opponentRecipeId) => startPractice(recipeId, opponentRecipeId),
    onPracticeDeck: (deckIndex) => startPractice(undefined, undefined, deckIndex),
    onStartDailyChallenge: (dailyChallengeId, learnerDeckIndex) => {
      if (!enabled) return;
      const playerId = ctx.playerId();
      if (playerId !== undefined) {
        ctx.client()?.sendCommand({
          kind: 'CardDuelPractice',
          entity: playerId,
          learnerDeckIndex,
          dailyChallengeId,
        });
      }
    },
    onShareCard: (card) => ctx.events.emit('cardShare', card),
    onCreateDuelListing: (deckIndex, wager) => {
      if (!enabled) return;
      const playerId = ctx.playerId();
      if (playerId !== undefined) {
        ctx.client()?.sendCommand({ kind: 'CardDuelListingCreate', entity: playerId, deckIndex, wager });
      }
    },
    onCancelDuelListing: () => {
      if (!enabled) return;
      const playerId = ctx.playerId();
      if (playerId !== undefined) ctx.client()?.sendCommand({ kind: 'CardDuelListingCancel', entity: playerId });
    },
    onAcceptDuelListing: (listingId, deckIndex) => {
      if (!enabled) return;
      const playerId = ctx.playerId();
      if (playerId !== undefined) {
        ctx.client()?.sendCommand({ kind: 'CardDuelListingAccept', entity: playerId, listingId, deckIndex });
      }
    },
  });

  const modal: PackOpeningHandle = (deps.initPackOpening ?? initPackOpening)({
    doc: ctx.ui,
    // The legendary gold-flash sting: the local player's own reward cue, full volume
    // (the cosmetic-award level-up cue, reused so no new bake is needed).
    onLegendary: () => ctx.audio.play({ sound: 'level-up', volume: 1 }),
  });
  const preview = (deps.initCardPreview ?? initCardPreview)({ doc: ctx.ui });
  ctx.events.on('cardView', (card) => preview.open(card));

  // The authoritative Binder state, pushed off the snapshot stream in reply to
  // OpenBinder and after a BuyCardPack / SaveCardDecks / OpenCardPack.
  ctx.events.on('cardState', (msg) => {
    binder.setState({
      reserved: msg.reserved,
      collection: msg.collection,
      decks: msg.decks,
      packs: msg.packs,
      starterRecipe: msg.starterRecipe,
      starterEligible: msg.starterEligible,
      dailyChallenges: msg.dailyChallenges,
      dailyChallengeEarnedGold: msg.dailyChallengeEarnedGold,
      dailyChallengeLimitGold: msg.dailyChallengeLimitGold,
      dailyChallengeResetsAt: msg.dailyChallengeResetsAt,
    });
  });

  ctx.events.on('cardDuelListings', (msg) => {
    binder.setDuelListings({ listings: msg.listings, yourListingId: msg.yourListingId });
  });

  // A successful claim gets one unmistakable private cue even when the Binder is
  // closed. The battle state opens its own gump separately; this toast never steals
  // focus and the shared mixer honours the player's SFX mute/volume settings.
  ctx.events.on('cardDuelListingAccepted', (msg) => {
    if (presentedListingAcceptances.has(msg.listingId)) return;
    presentedListingAcceptances.add(msg.listingId);
    ctx.audio.play({ sound: 'level-up', volume: 1 });
    if (!ctx.ui.body) return;
    let stack = ctx.ui.getElementById('card-duel-match-found');
    if (!stack) {
      stack = ctx.ui.createElement('div');
      stack.id = 'card-duel-match-found';
      stack.setAttribute('aria-live', 'assertive');
      ctx.ui.body.appendChild(stack);
    }
    const toast = ctx.ui.createElement('div');
    toast.className = 'card-duel-match-found-notification';
    toast.setAttribute('role', 'status');
    // The same private event goes to the host and the accepting player, so keep
    // the copy truthful for both seats while still naming the opponent and terms.
    toast.textContent = msg.wager > 0
      ? `Card duel found: ${msg.opponentName} — ${msg.wager.toLocaleString('en-US')} gold each!`
      : `Card duel found: ${msg.opponentName} — honor match!`;
    stack.appendChild(toast);
    ctx.ui.defaultView?.setTimeout(() => toast.remove(), 5000);
  });

  ctx.events.on('connection', () => presentedListingAcceptances.clear());

  // The five cards a just-opened pack revealed: show the flip modal. The accompanying
  // CardState (banking the cards + decrementing the count) arrives on its own event.
  ctx.events.on('packOpened', (cards, duplicateGold) => modal.reveal(cards, duplicateGold));

  // The toolbar Cards button opens the Binder. Unlike the wardrobe/notifications/
  // settings popovers it is a standalone draggable window, so it does not join the
  // toolbar's mutual-exclusion group - a plain open on click.
  const cardsButton = ctx.ui.getElementById('cards-button');
  cardsButton?.addEventListener('click', () => {
    if (enabled) binder.open();
  });

  // `/cards` is a client-side shortcut that opens the Binder (the `/dress` pattern) -
  // intercepted before it reaches the say path, so it never broadcasts.
  ctx.chatCommands.intercept((text) => {
    if (!enabled || text.trim().toLowerCase() !== '/cards') return false;
    binder.open();
    return true;
  });

  // The server-authoritative launch gate (ADR: gate the unreleased card game): while
  // off, the toolbar Cards button is hidden and the Binder entry points (button,
  // `/cards`, open/save/buy intents) no-op; the flip modal stays inert until the
  // server actually pushes card state.
  const setEnabled = (next: boolean): void => {
    enabled = next;
    if (cardsButton) {
      cardsButton.hidden = !enabled;
    }
    if (!enabled) binder.close();
  };
  ctx.events.on('cardsEnabled', setEnabled);
  setEnabled(false);
}
